/**
 * Button Color Configuration
 * 
 * This configuration allows you to set colors for primary and secondary buttons across the site.
 * 
 * Primary buttons: Used for main actions (Checkout, Apply Filters, Shop Now, Buy Now, etc.)
 * Secondary buttons: Used for less important actions (View Cart, Cancel, Secondary options, etc.)
 * 
 * For each button type, you can configure:
 * - background color
 * - text color
 * - hover background color
 * - hover text color
 * 
 * Example configurations:
 * 
 * Using hex colors:
 * {
 *   primary: {
 *     bg: '#24201f',           // Dark black/primary color
 *     text: '#ffffff',         // White text
 *     hoverBg: '#3a3633',      // Darker on hover
 *     hoverText: '#ffffff'
 *   },
 *   secondary: {
 *     bg: '#f5f5f5',           // Light gray background
 *     text: '#333333',         // Dark text
 *     hoverBg: '#e8e8e8',      // Slightly darker gray on hover
 *     hoverText: '#333333'
 *   }
 * }
 */
// Default static configuration
const defaultButtonColorConfig = {
  primary: {
    bg: '#ffffff',      // White background
    text: '#050505',    // Dark text
    hoverBg: '#0a0a0a', // Dark background on hover
    hoverText: '#ffffff', // White text on hover
  },
  secondary: {
    bg: '#050505',      // Dark background
    text: '#fdfcfc',    // Light text
    hoverBg: '#fafafa', // Light background on hover
    hoverText: '#333333', // Dark text on hover
  },
  tertiary: {
    bg: 'transparent',
    text: '#050505',    // Dark text
    hoverBg: '#F1F2F4',
    hoverText: '#050505',
  },
};

// This will be set dynamically by the theme system
let buttonColorConfig = { ...defaultButtonColorConfig };
/**
 * Get button color configuration
 * @returns {object} The entire button color configuration
 */
export const getButtonColorConfig = () => {
  return buttonColorConfig;
};

/**
 * Get primary button colors
 * @returns {object} Primary button colors {bg, text, hoverBg, hoverText}
 */
export const getPrimaryButtonColors = () => {
  return buttonColorConfig.primary;
};

/**
 * Get secondary button colors
 * @returns {object} Secondary button colors {bg, text, hoverBg, hoverText}
 */
export const getSecondaryButtonColors = () => {
  return buttonColorConfig.secondary;
};

/**
 * Get tertiary button colors
 * @returns {object} Tertiary button colors {bg, text, hoverBg, hoverText}
 */
export const getTertiaryButtonColors = () => {
  return buttonColorConfig.tertiary;
};

/**
 * Get CSS custom properties for button colors
 * @returns {object} CSS custom properties object
 */
export const getButtonColorCSSVariables = () => {
  return {
    '--btn-primary-bg': buttonColorConfig.primary.bg,
    '--btn-primary-text': buttonColorConfig.primary.text,
    '--btn-primary-hover-bg': buttonColorConfig.primary.hoverBg,
    '--btn-primary-hover-text': buttonColorConfig.primary.hoverText,
    '--btn-secondary-bg': buttonColorConfig.secondary.bg,
    '--btn-secondary-text': buttonColorConfig.secondary.text,
    '--btn-secondary-hover-bg': buttonColorConfig.secondary.hoverBg,
    '--btn-secondary-hover-text': buttonColorConfig.secondary.hoverText,
    '--btn-tertiary-bg': buttonColorConfig.tertiary.bg,
    '--btn-tertiary-text': buttonColorConfig.tertiary.text,
    '--btn-tertiary-hover-bg': buttonColorConfig.tertiary.hoverBg,
    '--btn-tertiary-hover-text': buttonColorConfig.tertiary.hoverText,
  };
};

/**
 * Update button color configuration from theme data
 * @param {object} themeData - Theme data with buttons object
 */
export const updateButtonColorConfigFromTheme = (themeData) => {
  if (themeData && themeData.buttons) {
    buttonColorConfig = {
      primary: {
        bg: themeData.buttons.primary?.bg || defaultButtonColorConfig.primary.bg,
        text: themeData.buttons.primary?.text || defaultButtonColorConfig.primary.text,
        hoverBg: themeData.buttons.primary?.hoverBg || defaultButtonColorConfig.primary.hoverBg,
        hoverText: themeData.buttons.primary?.hoverText || defaultButtonColorConfig.primary.hoverText,
      },
      secondary: {
        bg: themeData.buttons.secondary?.bg || defaultButtonColorConfig.secondary.bg,
        text: themeData.buttons.secondary?.text || defaultButtonColorConfig.secondary.text,
        hoverBg: themeData.buttons.secondary?.hoverBg || defaultButtonColorConfig.secondary.hoverBg,
        hoverText: themeData.buttons.secondary?.hoverText || defaultButtonColorConfig.secondary.hoverText,
      },
      tertiary: {
        bg: themeData.buttons.tertiary?.bg || defaultButtonColorConfig.tertiary.bg,
        text: themeData.buttons.tertiary?.text || defaultButtonColorConfig.tertiary.text,
        hoverBg: themeData.buttons.tertiary?.hoverBg || defaultButtonColorConfig.tertiary.hoverBg,
        hoverText: themeData.buttons.tertiary?.hoverText || defaultButtonColorConfig.tertiary.hoverText,
      },
    };
  }
};

export { buttonColorConfig as default };
